import { useSyncExternalStore } from "react";

import { readStorage, writeStorage } from "@/src/libs/storage";

export type Theme = "light" | "dark";

/** Clé de stockage — lue aussi par le script d'amorçage d'`index.html`. */
const STORAGE_KEY = "theme";

/** Classe attendue par HeroUI sur `<html>` (cf. `@heroui/styles`). */
const DARK_CLASS = "dark";

/**
 * Thème de l'admin.
 *
 * **La source de vérité est le DOM**, pas un `useState` : `<html>` porte déjà
 * la bonne classe quand React démarre, posée par le script inline
 * d'`index.html` (sans lui, la page clignoterait dans le mauvais thème). Un
 * état React en doublon finirait par diverger ; on lit donc le DOM à travers
 * `useSyncExternalStore`, ce qui garde tous les composants montés d'accord
 * sans avoir à traîner un provider de plus dans `App.tsx`.
 */
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle(DARK_CLASS, theme === "dark");
}

function getSnapshot(): Theme {
  return document.documentElement.classList.contains(DARK_CLASS)
    ? "dark"
    : "light";
}

/**
 * Même règle que le script d'amorçage : sans préférence système explicite pour
 * le clair, l'admin reste sombre.
 */
function systemTheme(query: MediaQueryList | MediaQueryListEvent): Theme {
  return query.matches ? "light" : "dark";
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  // Tant que l'utilisateur n'a pas tranché, on suit le système en direct.
  const media = globalThis.matchMedia?.("(prefers-color-scheme: light)");

  const onSystemChange = (event: MediaQueryListEvent) => {
    if (readStorage(STORAGE_KEY)) return;
    applyTheme(systemTheme(event));
    emit();
  };

  media?.addEventListener("change", onSystemChange);

  return () => {
    listeners.delete(listener);
    media?.removeEventListener("change", onSystemChange);
  };
}

/** Fixe le thème et le mémorise — le choix explicite gagne sur le système. */
export function setTheme(theme: Theme) {
  writeStorage(STORAGE_KEY, theme);
  applyTheme(theme);
  emit();
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot);

  return {
    theme,
    setTheme,
    toggleTheme: () => setTheme(theme === "dark" ? "light" : "dark"),
  };
}
